/**
 * @congress/platform-helpers v3.0.0
 * @version 3.0.0
 * @license CYIM: www.cyim.com
 */

import firebase from 'firebase/app';
import 'firebase/firestore';
import 'firebase/database';
import 'firebase/auth';
import Configuration from '../common/config.js';

/**
 * @module FirebaseHelper
 * @type {{getInstance: (function(*=, *=): Promise<{rtdb: *, FieldValue: *, db: *}|undefined>)}}
 */
const FirebaseHelper = function () {
  let instance;

  /**
   * Create an instance of FirebaseHelper
   * @throws {"VUE_APP_GRAPHQL_HTTP: A GraphQl http endpoint is required !"}
   * @throws {"VUE_APP_FIREBASE_OPTIONS: Firebase options are required !"}
   * @returns {Promise<{rtdb: firebase.database.Database, FieldValue: firebase.firestore.FieldValue, db: firebase.firestore.Firestore}>}
   */
  async function createInstance(firebaseOptions, graphQlUrl, forceDisableFirebaseAuth) {
    let firebaseApp;
    let authentication;
    if (!firebaseOptions && !Configuration.value("VUE_APP_FIREBASE_OPTIONS")) {
      throw "VUE_APP_FIREBASE_OPTIONS: Firebase options are required !";
    }
    try {
      const options = firebaseOptions || Configuration.value("VUE_APP_FIREBASE_OPTIONS");
      firebaseApp = !firebase.apps.length ? firebase.initializeApp(JSON.parse(atob(options))) : firebase.app();
      if (!forceDisableFirebaseAuth && (graphQlUrl || Configuration.value("VUE_APP_GRAPHQL_HTTP"))) {
        const url = graphQlUrl || Configuration.value("VUE_APP_GRAPHQL_HTTP");
        const token = await _getFirestoreToken(url);
        const firebaseAuth = firebaseApp.auth();
        authentication = await firebaseAuth.signInWithCustomToken(token);
        if (authentication) {
          return {
            db: firebaseApp.firestore(),
            rtdb: firebaseApp.database(),
            FieldValue: firebase.firestore.FieldValue,
            rtdbTimestamp: firebase.database.ServerValue.TIMESTAMP
          };
        }
      } else {
        console.warn("You may want to be firebase authenticated, if so please provide the graphQL url");
        return {
          db: firebaseApp.firestore(),
          rtdb: firebaseApp.database(),
          FieldValue: firebase.firestore.FieldValue,
          rtdbTimestamp: firebase.database.ServerValue.TIMESTAMP
        };
      }
    } catch (e) {
      console.error(e);
      throw new Error(e);
    }
  }

  /**
   * Fetch GraphQl to get a new token
   * @param graphQLEndpoint
   * @returns {Promise<null|string>}
   * @private
   */
  async function _getFirestoreToken(graphQLEndpoint) {
    const token = await _getAuthToken();
    let result = await fetch(graphQLEndpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': "Bearer " + token
      },
      body: JSON.stringify({
        query: `
                query {
                  firebaseToken
                }
            `
      })
    });
    if (result) {
      result = await result.json();
      return result?.data?.firebaseToken;
    }
    return null;
  }

  /**
   * Get token from AuthHelper
   * @returns {Promise<string>}
   * @private
   */
  async function _getAuthToken() {
    const token = await window?.AuthHelper?.getToken();
    return token || "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJjbGllbnRJZCI6Ijg1MzI2M2EyLTQyNDItNDlmOC04NTJhLThjMTI1M2U3ZDA4NiIsInBlcm1pc3Npb25zIjpbInJlYWRBbGwiLCJ3cml0ZUFsbCJdLCJzdWIiOiIxMjM0NTY3ODkwIiwibmFtZSI6IkN5aW0iLCJpYXQiOjE1MTYyMzkwMjJ9.AxOYR_dzjD1SwPMpj5V3u7zHmkCY0nrWEOdsiLFXhz0";
  }
  return {
    /**
     * Return instance of FirebaseHelper or create it
     * @returns {Promise<{rtdb: firebase.database.Database, FieldValue: firebase.firestore.FieldValue, db: firebase.firestore.Firestore}>}
     */
    getInstance: function () {
      let firebaseOptions = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : null;
      let graphQlUrl = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : null;
      let forceDisableFirebaseAuth = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : false;
      if (!instance) {
        instance = createInstance(firebaseOptions, graphQlUrl, forceDisableFirebaseAuth);
      }
      return instance;
    }
  };
}();
export { FirebaseHelper };
//# sourceMappingURL=index.js.map
